import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  InvoiceStatusEnum,
  TimesheetStatusEnum,
} from '@nxsys/constants/AppEnums';
import {
  useCancelTimesheet,
  useGenerateInvoice,
  usePerformReversalPayrolls,
} from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { timesheetsAction } from '@nxsys/store/slices/timesheets/reducer/timesheetsSlice';
import { GetListTimesheetsParams } from '@nxsys/store/slices/timesheets/type';
import {
  GenerateInvoiceParams,
  PerformReversalPayrollsParams,
} from '@nxsys/types/api';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { showMessage } from '@nxsys/utils/message';
import { Dropdown } from 'antd';
import { isEmpty, some } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import GenerateInvoiceModal, {
  GenerateInvoiceModalValues,
} from '../components/GenerateInvoiceModal';
import HeaderListing from '../components/HeaderListing';
import TimesheetReverseModal from '../components/TimesheetReverseModal';
import { useCanReversePayroll } from '@nxsys/hooks/useCanReversePayroll';
import { getAwaitingInvoiceColumns } from './column';

type Props = {
  cbAfterAction?: () => void;
  isDashboard?: boolean;
};

const AwaitingInvoiceGeneration: React.FC<Props> = ({
  cbAfterAction = () => {},
  isDashboard = false,
}) => {
  const { messages, formatMessage } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const theme = useTheme();

  const dispatch = useDispatch<AppDispatch>();
  const {
    loading,
    timesheetsData: { list, pagination, queryParams },
  } = useSelector((state: AppState) => state.timesheetsStore);
  const { payrollCountry, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const canReverse = useCanReversePayroll();

  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);

  const { run: cancelTimesheet, loading: isCancelTimesheetPending } =
    useCancelTimesheet();

  const {
    run: performReversalPayrolls,
    loading: isPerformReversalPayrollsPending,
  } = usePerformReversalPayrolls();

  const [reverseModal, setReverseModal] = useState<{
    isOpen: boolean;
    payrollIds: string[];
  }>({
    isOpen: false,
    payrollIds: [],
  });

  const toggleReverseModal = () => {
    setReverseModal((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  };

  const { run: generateInvoice, loading: isGenerateInvoicePending } =
    useGenerateInvoice();

  const [openGenerateInvoiceModal, setOpenGenerateInvoiceModal] =
    useState(false);

  const toggleGenerateInvoiceModal = () => {
    setOpenGenerateInvoiceModal(!openGenerateInvoiceModal);
  };

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['timesheet.awaitingInvoiceGeneration']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        {
          key: 'cancelTimesheet',
          label: messages['timesheet.cancel'],
          onClick: () => handleCancelSelected(),
        },
        ...(canReverse
          ? [
              {
                key: 'reverseTimesheet',
                label: messages['timesheet.reverse'],
                onClick: () => handleReverseSelected(),
              },
            ]
          : []),
      ],
    };

    return (
      <div
        className={`flex items-center gap-x-2 ${isDashboard ? 'mr-[48px]' : ''}`}
      >
        <AppButton
          name={messages['timesheet.generateInvoice']}
          type="primary"
          onClick={handleOpenGenerateInvoice}
        />
        <Dropdown menu={menuProps}>
          <AppButton
            type="primary"
            name={messages['common.action']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      </div>
    );
  };

  useEffect(() => {
    getTimeSheetList({
      page: 0,
      size: 20,
    });
  }, []);

  const getTimeSheetList = (params: GetListTimesheetsParams) => {
    const newParams = {
      ...params,
      invoiceStatus: InvoiceStatusEnum.AWAITING_INVOICING,
    };
    dispatch(timesheetsAction.getListTimesheets(newParams));
  };

  const refetchTimesheetList = () => {
    getTimeSheetList({ ...queryParams });
    cbAfterAction();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    getTimeSheetList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleFilter = (params: GetListTimesheetsParams) => {
    getTimeSheetList({
      ...queryParams,
      ...params,
      page: 0,
    });
  };

  const handleCancel = (list: TimeSheetDataItem[]) => {
    const validItems = list.filter(
      (item) =>
        item?.approveStatus !== TimesheetStatusEnum.CANCEL &&
        !item?.newDraftPayroll &&
        !item?.newPayroll,
    );
    const ids = validItems.map((item) => item.id);
    if (ids.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['timesheet.noValidTimesheetToCancel'],
      });
      return;
    }
    AppConfirmationDelete.open({
      title: messages['timesheet.cancel'],
      paragraph: formatMessage(
        { id: 'timesheet.cancelConfirm' },
        {
          timesheetList: (
            <span style={{ color: theme.palette.blue[500] }}>
              {ids.join(', ')}
            </span>
          ),
        },
      ),
      onConfirm: async () => {
        await cancelTimesheet(ids);
        showMessage({
          type: 'success',
          content: messages['timesheet.cancelSuccess'],
        });
        refetchTimesheetList();
      },
    });
  };

  const handleOpenReverse = (list: TimeSheetDataItem[]) => {
    const filteredList = list?.filter(
      (item) => !item.subcontractorRef?.isIssuedP45 || item.isPAL,
    );
    if (isEmpty(filteredList)) {
      showMessage({
        type: 'error',
        content: messages['timesheet.employeeAreLeaverPayrollCouldNotChange'],
      });
      return;
    }

    const tsDraftPayrolls = filteredList.filter(
      (ts) => !ts.newPayroll && ts.newDraftPayroll,
    );
    const tsPayrolls = filteredList.filter((ts) => ts.newPayroll);

    const draftPayrolls = tsDraftPayrolls.map((ts) => ts.newDraftPayroll);
    const payrolls = tsPayrolls.map((ts) => ts.newPayroll);

    let reversePayrollIds: string[] = [];

    if (!isEmpty(payrolls)) {
      reversePayrollIds = payrolls
        ?.map((p) => p?.id ?? '')
        .filter((id) => !!id);
    } else if (!isEmpty(draftPayrolls)) {
      handleReverse({
        draftPayrollIds: draftPayrolls
          .map((p) => p?.id ?? '')
          .filter((id) => !!id),
        payrollIds: [],
      });
    } else {
      const tsCancels = filteredList.filter(
        (ts) => !ts.newPayroll && !ts.newDraftPayroll,
      );
      if (!isEmpty(tsCancels)) {
        showMessage({
          type: 'error',
          content: messages['timesheet.timesheetAreNotProcessed'],
        });
        return;
      }
    }
    setReverseModal({
      isOpen: true,
      payrollIds: reversePayrollIds,
    });
  };

  const handleView = useCallback(
    (id: string) => {
      navigate(`/timesheets/${id}`, {
        query: { from: '/timesheets/awaiting-invoice-generation' },
      });
    },
    [navigate],
  );

  const handleCancelSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleCancel(selectedRows);
  };

  const handleReverseSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleOpenReverse(selectedRows);
  };

  useNotificationSocket({
    eventName: `reversal-payroll-from-timesheet-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['timesheet.reverseSuccess'],
      });
      refetchTimesheetList();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['timesheet.reverseFailed'],
      });
    },
  });

  const handleReverse = async (params: PerformReversalPayrollsParams) => {
    await performReversalPayrolls({
      ...params,
      isTimesheet: true,
    }).then(() => {
      showMessage({
        type: 'info',
        content: messages['timesheet.reverseSuccessPending'],
      });
    });
  };

  useNotificationSocket({
    eventName: `generate-invoice-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['timesheet.generateInvoiceSuccess'],
      });
      refetchTimesheetList();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['timesheet.generateInvoiceFailed'],
      });
    },
  });

  const handleOpenGenerateInvoice = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return false;
    }
    if (
      some(
        selectedRows,
        (item) => !!!item?.subcontractorRef?.companyPayment?.id,
      )
    ) {
      showMessage({
        type: 'error',
        content: messages['timesheet.includeTimesheetDoesNotHaveCompany'],
      });
      return false;
    }
    setOpenGenerateInvoiceModal(true);
  };

  const handleGenerate = (values: GenerateInvoiceModalValues) => {
    const params: GenerateInvoiceParams = {
      ids: selectedRows.map((item) => item.id),
      dueDateTimesheet: moment(values.dueDate).toISOString(),
      invoicedDate: moment(values.invoicedDate).toISOString(),
    };
    generateInvoice(params).then(() => {
      setOpenGenerateInvoiceModal(false);
      showMessage({
        type: 'info',
        content: messages['timesheet.generateInvoicePending'],
      });
      refetchTimesheetList();
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.startDate ||
    !!queryParams?.endDate ||
    !!queryParams?.batchFrom ||
    !!queryParams?.batchTo ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.timesheetId ||
    (queryParams?.agencyRefs && queryParams?.agencyRefs?.length > 0) ||
    (queryParams?.companyRefs && queryParams?.companyRefs?.length > 0);

  const renderTable = () => (
    <AppTableContainer
      rowKey="id"
      data={list ?? []}
      loading={loading}
      page={pagination.current}
      total={pagination.totalDocs}
      pageSize={pagination.pageSize}
      scroll={{ x: 2500, y: 700 }}
      titleEmpty={messages['table.empty']}
      columns={getAwaitingInvoiceColumns({
        messages,
        payrollCountry,
        handleCancel,
        handleOpenReverse,
        handleView,
        theme,
        isBureau,
        hideReverse: !canReverse,
      })}
      onChangePagination={onChangePagination}
      rowSelection={{
        type: 'checkbox',
        onChange: (_: React.Key[], selectedRows: any[]) => {
          setSelectedRows(selectedRows);
        },
      }}
      noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
    />
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isCancelTimesheetPending ||
        isPerformReversalPayrollsPending ||
        isGenerateInvoicePending
      }
    >
      {isDashboard ? (
        <>
          <div className="mb-[20px]">
            <HeaderListing onFilter={handleFilter} isAwaitingInvoice={true} />
          </div>
          <AppsContent>{renderTable()}</AppsContent>
        </>
      ) : (
        <AppCard>
          <AppsHeader>
            <HeaderListing onFilter={handleFilter} isAwaitingInvoice={true} />
          </AppsHeader>
          <AppsContent>{renderTable()}</AppsContent>
        </AppCard>
      )}

      <TimesheetReverseModal
        isOpen={reverseModal.isOpen}
        toggle={toggleReverseModal}
        payrollIds={reverseModal.payrollIds}
        onPerform={handleReverse}
      />
      <GenerateInvoiceModal
        isOpen={openGenerateInvoiceModal}
        toggle={toggleGenerateInvoiceModal}
        onGenerate={handleGenerate}
        isGenerateInvoicePending={isGenerateInvoicePending}
      />
    </AppsContainer>
  );
};

export default React.memo(AwaitingInvoiceGeneration);
